// The actor-name SLOT — how ANY module's provenance line shows WHO touched a
// record without importing @erp/module-auth.
//
// WHY THIS EXISTS (audit UX-06, decision D14, 2026-09-19): every durable row
// carries created_by / updated_by / approver as an opaque user id, and six
// detail screens across LIB and FIN printed that id verbatim — "Created 7 days
// ago by F6CplarTlUGncbh9jwH…" — each behind its own private `ActorId` helper.
// A reader cannot act on a 21-character string; they can act on "Ana Rao".
//
// The name lives in the auth module's directory (GET /api/auth/directory), and
// a LIB screen may not import auth (ADR-001 Fig 4.1: no module → module edge).
// So the module declares the HOLE — `<ActorName id={…}/>` — and the shell fills
// it with auth's resolver (`ActorDirectoryProvider`), exactly the seam shape of
// `@erp/ui/record-history`. Unfilled, the default resolves nothing and the
// component shows the id it was given, truncated, with the full value on
// hover: the honest fallback rather than an invented name.
import { createContext, useContext, type Accessor } from "solid-js";

/** One person as the directory answers: a name when there is one, else the address. */
export interface ActorIdentity {
  id: string;
  name: string | null;
  email: string | null;
}

/**
 * The shell's resolver: an accessor for the id in, an accessor for the
 * identity out — `undefined` while it loads or when the id is unknown (the
 * account was deleted and the record outlived it). Called inside the
 * consuming component's reactive scope, so an implementation may own a query.
 */
export type ActorResolver = (id: Accessor<string>) => Accessor<ActorIdentity | undefined>;

/** Resolves nothing: a deployment without a directory shows ids, not an error. */
const NO_RESOLVER: ActorResolver = () => () => undefined;

const ActorDirectoryContext = createContext<ActorResolver>(NO_RESOLVER);

/** The shell wraps its routes in this, passing @erp/module-auth/fe's `resolveActor`. */
export const ActorDirectoryProvider = ActorDirectoryContext.Provider;

/** Call in a component body when a screen needs the identity itself (a lookup control, a CSV cell). */
export const useActorResolver = () => useContext(ActorDirectoryContext);

/**
 * The directory's other half: people whose name or address contains the
 * needle, for a filter or a picker ("who did this?" typed as a name, never
 * as an id — audit UX-19). Capped by the implementation (auth's is 20).
 */
export type ActorSearch = (query: string, limit?: number) => Promise<ActorIdentity[]>;

/** Finds nobody: a deployment without a directory offers no lookup, not an error. */
const NO_SEARCH: ActorSearch = async () => [];

const ActorSearchContext = createContext<ActorSearch>(NO_SEARCH);

/** The shell wraps its routes in this too, passing @erp/module-auth/fe's `searchActors`. */
export const ActorSearchProvider = ActorSearchContext.Provider;

/** Call in a component body; hand the result to a LookupSelect's `search`. */
export const useActorSearch = () => useContext(ActorSearchContext);

/** What to print for an identity: the name, else the address, else the id. */
export const actorLabel = (who: ActorIdentity | undefined, id: string): string => who?.name || who?.email || id;

/**
 * "by Ana Rao" — an opaque actor id rendered as the person it names. While
 * the directory answers, and whenever it cannot, the id itself shows in mono,
 * truncated, with the full value as the title. A resolved name carries the
 * address (and the id) on hover so an audit conversation can still pin it.
 */
export function ActorName(props: {
  /** The opaque user id a record carries in created_by / updated_by / approver. */
  id: string;
  /** Automation id on the element. */
  elementId?: string;
  /** Extra classes — spacing only. */
  class?: string;
}) {
  const resolve = useActorResolver();
  const who = resolve(() => props.id);
  const resolved = () => Boolean(who()?.name || who()?.email);
  const hover = () => {
    const w = who();
    if (!w) return props.id;
    return [w.name, w.email, w.id].filter((s): s is string => Boolean(s)).join(" · ");
  };
  return (
    <span
      id={props.elementId}
      title={hover()}
      data-resolved={resolved() ? "true" : "false"}
      class={`inline-block max-w-[12rem] truncate align-bottom ${
        resolved() ? "" : "font-mono text-xs text-base-subtle"
      } ${props.class ?? ""}`}
    >
      {actorLabel(who(), props.id)}
    </span>
  );
}
